import { useEffect, useState } from 'react'
import ReactSyntaxHighlighter from 'react-syntax-highlighter'
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism'

import { Button } from '@ui/components/core'

interface ImportedEvent {
  data: unknown
  error: string | null
}

interface ImportSampleEventProps {
  handleSave: (data: unknown) => Promise<void>
  loading: boolean
}

export const ImportSampleEvent = ({ handleSave, loading }: ImportSampleEventProps) => {
  const [importedEvent, setImportedEvent] = useState<ImportedEvent>({ data: null, error: null })

  useEffect(() => {
    document.getElementById('import-event-dropzone')?.focus()
  }, [])

  const handleImportEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string)
          setImportedEvent({ data, error: null })
        } catch (err) {
          setImportedEvent({ data: null, error: 'Invalid JSON format' })
        }
      }
      reader.readAsText(file)
    }
  }

  const handlePasteEvent = (event: React.ClipboardEvent<HTMLDivElement>) => {
    try {
      const text = event.clipboardData.getData('text')
      const data = JSON.parse(text)
      setImportedEvent({ data, error: null })
    } catch (err) {
      setImportedEvent({ data: null, error: 'Invalid JSON format' })
    }
  }

  const handleCancel = () => {
    setImportedEvent({ data: null, error: null })
  }

  return (
    <div className="flex w-full flex-col space-y-4 px-6 py-4">
      <div
        id="import-event-dropzone"
        data-testid="import-event-dropzone"
        className="relative flex h-80 w-full cursor-pointer flex-col items-center justify-center rounded-md border border-gray-300 bg-gray-100 p-4"
        onClick={() => document.getElementById('import-event-file')?.click()}
        onPaste={handlePasteEvent}
        tabIndex={0}>
        {importedEvent.data ? (
          <ReactSyntaxHighlighter
            language="json"
            style={atomDark}
            className="scrollbar-hide size-full rounded-md text-xs">
            {JSON.stringify(importedEvent.data, null, 2)}
          </ReactSyntaxHighlighter>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <h1 className="rounded-lg border-2 border-dashed border-gray-300 p-4 text-lg font-medium text-gray-500">
              Paste your events here or upload sample file
            </h1>
            {importedEvent.error && <p className="text-sm text-red-500">{importedEvent.error}</p>}
          </div>
        )}
        <input
          type="file"
          className="hidden"
          data-testid="import-event-file"
          id="import-event-file"
          onChange={handleImportEvent}
          accept=".json"
        />
      </div>

      <div className="flex w-full flex-row items-center justify-end space-x-2">
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => handleSave(importedEvent.data)}
          disabled={!importedEvent.data || loading}
          loading={loading}>
          Import
        </Button>
      </div>
    </div>
  )
}
